import { useNavigate } from "react-router-dom";
import { useState, useEffect, useReducer, useMemo } from "react";
import styled from "@emotion/styled";

import MainLayout from "src/layouts/MainLayout";
import { useAuth } from "src/context";
import { useDealerUserInfo } from "src/context/DealerUserInfoContext";
import SearchDealers from "./SearchDealers";
import ObjectTable from "./ObjectTable";
import PaginationControls from "src/components/PaginationControls";
import { useDebounce } from "src/hooks/useDebounce";
import { editDealerDataSaveHandler } from "./api";
import EditDealerPage from "./EditDealerPage";
import { useSafeApiCall, useApiClient } from "src/hooks/api";
import { PrimaryButton } from "src/components/Buttons";

export interface Dealer {
  id: string;
  name: string;
  [key: string]: string | string[];
}

export interface DealerFormData {
  name: string;
  [key: string]: string | string[];
}

const ButtonWrapper = styled.div`
  margin-bottom: 20px;
`;

export default () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { dealerUserInfo } = useDealerUserInfo();
  const [loadData, reloadData] = useReducer((x) => !x, false);
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [filters, setFilters] = useState<{ field: string; value: string }[]>(
    [],
  );
  const [activeDealer, setActiveDealer] = useState<object>();
  const [objectKeys, setObjectKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedFilters = useDebounce(filters, 300);
  const filteredDealers = useMemo(() => {
    if (debouncedFilters.length === 0) return dealers;
    return dealers.filter((dealer) =>
      debouncedFilters.every(({ field, value }) => {
        const dealerValue = dealer[field];
        if (typeof dealerValue !== "string") return false;
        return dealerValue.toLowerCase().includes(value.toLowerCase());
      }),
    );
  }, [dealers, debouncedFilters]);
  const { fetchFromApi } = useApiClient();
  const safeApiCall = useSafeApiCall();

  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const pagedDealers = useMemo(() => {
    return filteredDealers.slice(offset, offset + pageSize);
  }, [filteredDealers, offset, pageSize]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedFilters, pageSize]);

  useEffect(() => {
    if (dealerUserInfo.id && !dealerUserInfo.is_admin) {
      void navigate("/error", { state: { status: 403 }, replace: true });
      return;
    }

    const path = "v1/dealers";
    safeApiCall(() => fetchFromApi(path))
      .then((response) => {
        const data = response as Dealer[];
        if (data.length > 0) {
          setObjectKeys(Object.keys(data[0]));
        }

        setDealers(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [loadData, dealerUserInfo, navigate, user]);

  const openEditDealerScreen = (dealer: Dealer) => {
    setActiveDealer(dealer);
  };

  const createNewDealerClickHandler = () => {
    const keys = Object.keys(dealers[0]);
    const newDealerObject = Object.fromEntries(
      keys.map((key) => [key, key === "document_copies_email_list" ? [] : ""]),
    );
    setActiveDealer(newDealerObject);
  };

  return activeDealer ? (
    <EditDealerPage
      dealerData={activeDealer as Dealer}
      closeHandler={() => setActiveDealer(undefined)}
      saveHandler={(dealerFormData: DealerFormData, isNewDealer: boolean) =>
        editDealerDataSaveHandler({
          dealerFormData,
          isNewDealer,
          activeDealer: activeDealer as Dealer,
          setActiveDealer,
          reloadData,
          user,
        })
      }
      isNewDealer={(activeDealer as Dealer).id === ""}
    />
  ) : (
    <MainLayout page="dealers" showHeading>
      {dealerUserInfo.id && dealerUserInfo.is_admin ? (
        <>
          {dealers.length > 0 && (
            <ButtonWrapper>
              <PrimaryButton onClick={createNewDealerClickHandler}>
                Create New Dealer
              </PrimaryButton>
            </ButtonWrapper>
          )}
          <SearchDealers
            loading={loading}
            filters={filters}
            setFilters={setFilters}
            objectKeys={objectKeys}
          />
          <ObjectTable
            data={pagedDealers}
            filters={filters}
            objectKeys={objectKeys}
            editButtonHandler={openEditDealerScreen}
          />
          <PaginationControls
            offset={offset}
            setOffset={setOffset}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalCount={filteredDealers.length}
          />
        </>
      ) : (
        <p>No data to display.</p>
      )}
    </MainLayout>
  );
};
